import React, { FC, useCallback, useEffect, useState } from 'react';

import { Notice, StatusComponent } from '@/apps/health/status/types/types';
import SvgClose from '@/assets/icons/Close';
import Info from '@/assets/icons/Info';
import WarningTriangleOrange from '@/assets/icons/WarningTriangleOrange';
import type { PushMeldingApi } from '@/components/dialogs/push-melding/type';
import useApiErrorStatus from '@/hooks/useApiErrorStatus';
import { SSEConnectionStatus } from '@/queries/sse/useServerSentEvents';
import { useFetchWithAuth } from '@/queries/useFetchWithAuth';

import styles from './ApiConnectionStatus.module.css';

type SseConnectionStatusProps = {
  sseConnectionStatus: SSEConnectionStatus;
  isAuthenticated: boolean;
};

export const parseDate = (value: string | null | undefined): Date | null => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const isPushMeldingActiveNow = (pm: PushMeldingApi, now = new Date()): boolean => {
  if (!pm.message?.trim()) return false;

  // startDateTime must exist and must be <= now
  const start = parseDate(pm.startDateTime);
  if (!start || start.getTime() > now.getTime()) return false;

  // if endDateTime exists, it must be >= now
  const end = parseDate(pm.endDateTime);
  return !(end && end.getTime() < now.getTime());
};

const ApiConnectionStatus: FC<SseConnectionStatusProps> = ({ sseConnectionStatus, isAuthenticated }) => {
  const { hasApiError } = useApiErrorStatus();
  const [isDismissed, setIsDismissed] = useState(false);

  const { fetchWithAuth } = useFetchWithAuth();
  const [notice, setNotice] = useState<Notice>();
  const [pushMelding, setPushMelding] = useState<PushMeldingApi | null>(null);

  const fetchHealthStatus = useCallback(async () => {
    try {
      const response = await fetchWithAuth('/actuator/health');
      const health = (await response.json()) as StatusComponent;
      setNotice(health.notice);
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error('[Health Status Error]', err);
      }
      // swallow in production
    }
  }, [fetchWithAuth]);

  const fetchPushMelding = useCallback(async () => {
    try {
      const response = await fetchWithAuth('/push-meldingen/');

      if (!response.ok) {
        setPushMelding(null);
        return;
      }

      const responseText = await response.text();
      if (!responseText) {
        setPushMelding(null);
        return;
      }

      let data: PushMeldingApi | null = null;

      try {
        data = JSON.parse(responseText) as PushMeldingApi;
      } catch (parseErr) {
        if (import.meta.env.DEV) {
          console.error('[PushMelding JSON parse error]', parseErr, responseText);
        }
        setPushMelding(null);
        return;
      }

      // ✅ time-window + shape validation
      if (data && data.id && data.message && isPushMeldingActiveNow(data)) {
        setPushMelding(data);
      } else {
        setPushMelding(null);
      }
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error('[PushMelding Error]', err);
      }
      setPushMelding(null);
    }
  }, [fetchWithAuth]);

  useEffect(() => {
    if (!isAuthenticated && !import.meta.env?.DEV) {
      setNotice(undefined);
      setPushMelding(null);
      return;
    }

    void fetchPushMelding();
    void fetchHealthStatus();

    const interval = setInterval(() => {
      void fetchPushMelding();
      void fetchHealthStatus();
    }, 5000);

    return () => clearInterval(interval);
  }, [isAuthenticated, fetchPushMelding, fetchHealthStatus]);

  const activeMessage = pushMelding?.message ?? notice?.message;
  const isPushActive = !!pushMelding?.message;

  const bannerKey = isPushActive
    ? `${pushMelding?.id}-${pushMelding?.message}-${pushMelding?.priority}-${pushMelding?.startDateTime}-${pushMelding?.endDateTime}`
    : notice?.message;

  const isInfo = isPushActive && pushMelding?.priority?.toUpperCase() === 'INFO';

  const bg = isInfo ? 'var(--tractie-color-background-info-subtle)' : 'var(--tractie-color-background-warning-subtle)';

  const stripe = isInfo
    ? 'var(--tractie-color-background-info-default)'
    : 'var(--tractie-color-background-warning-default)';

  const textColor = 'var(--tractie-color-content-default)';

  useEffect(() => {
    if (bannerKey) {
      setIsDismissed(false);
    }
  }, [bannerKey]);

  const hasError = hasApiError(sseConnectionStatus);
  const isBannerVisible =
    (isAuthenticated || import.meta.env?.DEV) && activeMessage && !isDismissed && (isPushActive || hasError);

  useEffect(() => {
    const root = document.documentElement;
    if (isBannerVisible) {
      root.classList.add('mwd-has-api-banner');
    } else {
      root.classList.remove('mwd-has-api-banner');
      root.classList.add('mwd-no-api-banner');
    }

    return () => {
      root.classList.remove('mwd-has-api-banner');
    };
  }, [isBannerVisible]);

  const renderIcon = () => {
    if (isInfo) {
      return (
        <Info
          width='24px'
          height='24px'
          fill='var(--tractie-color-content-info)'
          style={{ paddingTop: '2px' }}
          data-testid='info-icon'
        />
      );
    }

    return (
      <WarningTriangleOrange
        width='24px'
        height='24px'
        fill='var(--tractie-color-content-warning)'
        style={{ paddingTop: '2px' }}
        data-testid='warning-icon'
      />
    );
  };

  const renderText = () => {
    if (isPushActive) {
      return <p>{pushMelding?.message}</p>;
    }

    return <p>Let op: {notice?.message}</p>;
  };

  return (
    <>
      {isBannerVisible && (
        <div className={`${styles.sseStatus} ${styles.sseStatusVisible}`}>
          <div
            style={{
              backgroundColor: bg,
              borderLeft: `solid 1px var(--tractie-color-border-default)`,
            }}
          >
            <div
              className={styles.sseStatusMessagebar}
              style={{
                borderLeft: `solid 4px ${stripe}`,
              }}
            >
              <div>{renderIcon()}</div>

              <div className={styles.sseStatusText} style={{ color: textColor }}>
                {renderText()}
              </div>

              <button onClick={() => setIsDismissed(true)} className={styles.closeButton} aria-label='Sluiten'>
                <SvgClose width='24px' height='24px' style={{ fill: 'var(--tractie-color-content-subtle)' }} />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ApiConnectionStatus;
